/**
 * TeamPage — roster of every active person in the org + bulk invite.
 *
 * Surfaces for supervisor / contractor_admin / super_admin:
 *   - top: read-only list of current team grouped by role
 *   - bottom: invite form (paste emails, pick role, submit)
 *
 * The invite form calls the provision-users Edge Function via
 * useInviteTeam; the response carries provisioned + failed arrays and
 * we render both. A new profile row is created by the handle_new_user
 * trigger (post audit P0 #3 fix) so the org-team query auto-refreshes.
 *
 * No promote / demote UI yet — that's P1 #6 in the audit and ships as
 * its own page. This v1 surface is "see the team, add to the team."
 */

import { useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgTeam, type TeamMemberRow } from '@/hooks/useOrgTeam'
import { useInviteTeam, type InviteRole, type InviteTeamResult } from '@/hooks/useInviteTeam'
import { useUpdateUserRole } from '@/hooks/useUpdateUserRole'
import { useRole } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/authStore'
import { isAppError } from '@/lib/errors/AppError'

const PROMOTE_ROLES = new Set(['contractor_admin', 'super_admin'])

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ROLE_ORDER: ReadonlyArray<{ role: string; label: string }> = [
  { role: 'super_admin',      label: 'Platform admin' },
  { role: 'contractor_admin', label: 'Contractor admin' },
  { role: 'supervisor',       label: 'Supervisors' },
  { role: 'cleaner',          label: 'Cleaners' },
  { role: 'maintenance',      label: 'Maintenance' },
  { role: 'client_view',      label: 'Client view' },
]

export function TeamPage(): ReactNode {
  const { t }    = useTranslation()
  const team     = useOrgTeam()
  const invite   = useInviteTeam()
  const updateRole   = useUpdateUserRole()
  const callerRole   = useRole() ?? ''
  const callerUserId = useAuthStore((s) => s.user?.id ?? null)
  const canPromote   = PROMOTE_ROLES.has(callerRole)

  const [emailsText, setEmailsText] = useState('')
  const [role,       setRole]       = useState<InviteRole>('cleaner')
  const [lastResult, setLastResult] = useState<InviteTeamResult | null>(null)
  const [rejected,   setRejected]   = useState<string[]>([])

  function parseEmails(input: string): { ok: string[]; bad: string[] } {
    const candidates = input
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
    const seen = new Set<string>()
    const ok: string[] = []
    const bad: string[] = []
    for (const e of candidates) {
      if (seen.has(e)) continue
      seen.add(e)
      if (EMAIL_RE.test(e)) ok.push(e)
      else                  bad.push(e)
    }
    return { ok, bad }
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    const { ok, bad } = parseEmails(emailsText)
    setRejected(bad)
    if (ok.length === 0) return
    try {
      const result = await invite.mutateAsync({ emails: ok, role })
      setLastResult(result)
      // Clear the textarea on a clean run (zero failures); keep it
      // populated if some failed so the admin can correct + resend.
      if (result.failed.length === 0) setEmailsText('')
    } catch {
      // AppError surfaces below via invite.error — no toast.
    }
  }

  const grouped = groupByRole(team.data ?? [])

  return (
    <article style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: '52rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('team.title', 'Team')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.9rem' }}>
          {t('team.subtitle', 'Everyone in your organization. Invite cleaners and supervisors below — they’ll get a sign-in PIN by email.')}
        </p>
      </header>

      {/* ── Current team ──────────────────────────────────────── */}
      <section
        aria-labelledby="team-roster"
        style={{
          background:   'var(--brand-white)',
          border:       '1px solid var(--brand-neutral-300)',
          borderRadius: 'var(--brand-radius-lg)',
          padding:      '1rem',
        }}
      >
        <h2 id="team-roster" style={{ margin: 0, fontSize: '1rem' }}>
          {t('team.roster', 'Current team')}
          {team.data && (
            <span style={{ color: 'var(--brand-neutral-500)', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
              ({team.data.length})
            </span>
          )}
        </h2>
        {team.isLoading && (
          <p style={{ color: 'var(--brand-neutral-500)', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
            {t('team.loading', 'Loading team…')}
          </p>
        )}
        {team.error && (
          <p role="alert" style={{ color: 'var(--brand-danger)', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
            {team.error instanceof Error ? team.error.message : t('team.loadFailed', 'Failed to load team.')}
          </p>
        )}
        {!team.isLoading && !team.error && team.data && team.data.length === 0 && (
          <p style={{ color: 'var(--brand-neutral-500)', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
            {t('team.empty', 'No team members yet. Invite the first one below.')}
          </p>
        )}

        {ROLE_ORDER.map((g) => {
          const rows = grouped[g.role] ?? []
          if (rows.length === 0) return null
          return (
            <div key={g.role} data-testid={`team-group-${g.role}`} style={{ marginTop: '0.85rem' }}>
              <h3
                style={{
                  margin:       0,
                  fontSize:     '0.8rem',
                  color:        'var(--brand-neutral-500)',
                  textTransform:'uppercase',
                  letterSpacing:'0.05em',
                }}
              >
                {g.label}
              </h3>
              <ul style={{ margin: '0.3rem 0 0', padding: 0, listStyle: 'none' }}>
                {rows.map((row) => {
                  // Promote / demote button surfaces only for:
                  //   - admin viewer (contractor_admin / super_admin)
                  //   - rows that are cleaner or supervisor (other roles
                  //     are out of scope for this minimal UI)
                  //   - NOT the viewer's own row (server refuses anyway,
                  //     but hiding the button avoids a confusing click)
                  const isSelf      = row.id === callerUserId
                  const isToggle    = row.role === 'cleaner' || row.role === 'supervisor'
                  const showToggle  = canPromote && isToggle && !isSelf
                  const targetRole  = row.role === 'cleaner' ? 'supervisor' : 'cleaner'
                  const buttonLabel = row.role === 'cleaner'
                    ? t('team.promote',  'Make supervisor')
                    : t('team.demote',   'Demote to cleaner')
                  const busy        = updateRole.isPending && updateRole.variables?.userId === row.id
                  const rowErr      = updateRole.error
                                      && isAppError(updateRole.error)
                                      && updateRole.variables?.userId === row.id
                                      ? updateRole.error.userMessage
                                      : null
                  return (
                    <li
                      key={row.id}
                      data-testid="team-row"
                      style={{
                        display:        'flex',
                        flexDirection:  'column',
                        gap:            '0.2rem',
                        padding:        '0.35rem 0',
                        borderBottom:   '1px solid var(--brand-neutral-100)',
                        fontSize:       '0.9rem',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{row.full_name || row.email.split('@')[0]}</span>
                        <span style={{ color: 'var(--brand-neutral-500)', flex: 1, textAlign: 'right' }}>{row.email}</span>
                        {showToggle && (
                          <button
                            type="button"
                            data-testid={`team-role-toggle-${row.id}`}
                            disabled={busy}
                            onClick={() => updateRole.mutate({ userId: row.id, newRole: targetRole })}
                            style={{
                              fontSize:     '0.75rem',
                              padding:      '0.2rem 0.55rem',
                              borderRadius: 'var(--brand-radius-sm)',
                              border:       '1px solid var(--brand-neutral-300)',
                              background:   'var(--brand-white)',
                              color:        'var(--brand-neutral-700)',
                              cursor:       busy ? 'default' : 'pointer',
                              opacity:      busy ? 0.5 : 1,
                              whiteSpace:   'nowrap',
                            }}
                          >
                            {busy ? t('team.updating', 'Saving…') : buttonLabel}
                          </button>
                        )}
                      </div>
                      {rowErr && (
                        <p role="alert" data-testid={`team-role-error-${row.id}`}
                           style={{ color: 'var(--brand-danger)', fontSize: '0.75rem', margin: 0 }}>
                          {rowErr}
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </section>

      {/* ── Invite form ───────────────────────────────────────── */}
      <section
        aria-labelledby="team-invite"
        style={{
          background:   'var(--brand-white)',
          border:       '1px solid var(--brand-neutral-300)',
          borderRadius: 'var(--brand-radius-lg)',
          padding:      '1rem',
        }}
      >
        <h2 id="team-invite" style={{ margin: 0, fontSize: '1rem' }}>
          {t('team.inviteTitle', 'Invite team members')}
        </h2>
        <p style={{ margin: '0.25rem 0 0.75rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
          {t(
            'team.inviteHelp',
            'Paste one email per line (or separate with commas). Up to 50 at a time. They’ll each get a 6-digit PIN by email and can sign in immediately.',
          )}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <label htmlFor="team-emails" style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
            {t('team.emailsLabel', 'Email addresses')}
          </label>
          <textarea
            id="team-emails"
            data-testid="team-emails"
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            rows={5}
            placeholder={`pat@example.com${'\n'}sam@example.com`}
            disabled={invite.isPending}
            style={{
              fontSize:     '0.9rem',
              padding:      '0.5rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              resize:       'vertical',
              fontFamily:   'inherit',
            }}
          />

          <label htmlFor="team-role" style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
            {t('team.roleLabel', 'Role')}
          </label>
          <select
            id="team-role"
            data-testid="team-role"
            value={role}
            onChange={(e) => setRole(e.target.value as InviteRole)}
            disabled={invite.isPending}
            style={{
              fontSize:     '0.9rem',
              padding:      '0.45rem 0.55rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              background:   'var(--brand-white)',
              maxWidth:     '12rem',
            }}
          >
            <option value="cleaner">{t('team.roleCleaner', 'Cleaner')}</option>
            <option value="supervisor">{t('team.roleSupervisor', 'Supervisor')}</option>
          </select>

          {rejected.length > 0 && (
            <p role="alert" data-testid="team-invalid" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
              {t('team.invalidEmails', 'These addresses don’t look like valid emails: {{list}}', { list: rejected.join(', ') })}
            </p>
          )}

          {invite.error && isAppError(invite.error) && (
            <p role="alert" data-testid="team-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
              {invite.error.userMessage}
            </p>
          )}

          <div>
            <button
              type="submit"
              data-testid="team-submit"
              disabled={invite.isPending || emailsText.trim().length === 0}
              style={{
                background:   'var(--brand-primary)',
                color:        'var(--brand-white)',
                border:       'none',
                borderRadius: 'var(--brand-radius-md)',
                padding:      '0.55rem 1.1rem',
                fontSize:     '0.9rem',
                fontWeight:   600,
                cursor:       (invite.isPending || emailsText.trim().length === 0) ? 'default' : 'pointer',
                opacity:      (invite.isPending || emailsText.trim().length === 0) ? 0.6 : 1,
              }}
            >
              {invite.isPending ? t('team.inviting', 'Sending…') : t('team.invite', 'Send invites')}
            </button>
          </div>
        </form>

        {lastResult && (
          <div data-testid="team-result" style={{ marginTop: '0.85rem', fontSize: '0.85rem' }}>
            {lastResult.provisioned.length > 0 && (
              <p style={{ margin: 0, color: 'var(--brand-accent)' }}>
                ✅ {t('team.provisionedCount', 'Invited {{count}}', { count: lastResult.provisioned.length })}
                {' '}— {lastResult.provisioned.join(', ')}
              </p>
            )}
            {lastResult.failed.length > 0 && (
              <ul style={{ margin: '0.4rem 0 0', padding: 0, listStyle: 'none' }}>
                {lastResult.failed.map((f) => (
                  <li key={f.email} style={{ color: 'var(--brand-danger)' }}>
                    ❌ {f.email} — {f.code}: {f.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </article>
  )
}

function groupByRole(rows: TeamMemberRow[]): Record<string, TeamMemberRow[]> {
  // Fan out: each multi-hat user appears once under EVERY role they
  // hold via profile_roles (Maria → contractor_admin AND supervisor
  // AND cleaner). Fall back to the primary profiles.role for any user
  // whose profile_roles backfill is empty.
  const out: Record<string, TeamMemberRow[]> = {}
  for (const row of rows) {
    const roles = row.roles && row.roles.length > 0 ? row.roles : [row.role ?? 'cleaner']
    for (const r of roles) {
      if (!out[r]) out[r] = []
      out[r].push(row)
    }
  }
  return out
}
